import { type CSSProperties, useEffect, useRef } from "react"
import { cn } from "@/lib/utils"

type Props = {
  className?: string
  style?: CSSProperties
  opacity?: number
  gap?: number
  radius?: number
  colorLightVar?: string
  glowColorLightVar?: string
  colorDarkVar?: string
  glowColorDarkVar?: string
  backgroundOpacity?: number
  speedMin?: number
  speedMax?: number
  speedScale?: number
}

export function DottedGlowBackground({
  className,
  style,
  opacity = 1,
  gap = 12,
  radius = 1.5,
  colorLightVar = "--color-neutral-500",
  glowColorLightVar = "--color-neutral-600",
  colorDarkVar = "--color-neutral-500",
  glowColorDarkVar = "--color-sky-800",
  backgroundOpacity = 0,
  speedMin = 0.3,
  speedMax = 1.6,
  speedScale = 1,
}: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext("2d")
    if (!ctx) return

    let raf = 0
    type Dot = { x: number; y: number; phase: number; speed: number }
    let dots: Array<Dot> = []

    const isDark = () =>
      document.documentElement.classList.contains("dark") ||
      window.matchMedia("(prefers-color-scheme: dark)").matches

    const getCssColor = (varName: string, fallback = "#666666") => {
      const value = getComputedStyle(document.documentElement)
        .getPropertyValue(varName)
        .trim()
      return value || fallback
    }

    const setup = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2)
      const rect = canvas.getBoundingClientRect()
      canvas.width = Math.max(1, Math.floor(rect.width * dpr))
      canvas.height = Math.max(1, Math.floor(rect.height * dpr))
      ctx.setTransform(1, 0, 0, 1, 0, 0)
      ctx.scale(dpr, dpr)

      const next: Array<Dot> = []
      const cols = Math.max(1, Math.floor(rect.width / gap))
      const rows = Math.max(1, Math.floor(rect.height / gap))
      const offsetX = (rect.width - (cols - 1) * gap) / 2
      const offsetY = (rect.height - (rows - 1) * gap) / 2
      for (let i = 0; i < cols; i++) {
        for (let j = 0; j < rows; j++) {
          next.push({
            x: offsetX + i * gap,
            y: offsetY + j * gap,
            phase: Math.random() * Math.PI * 2,
            speed: speedMin + Math.random() * Math.max(0, speedMax - speedMin),
          })
        }
      }
      dots = next
    }

    const draw = (now: number) => {
      const rect = canvas.getBoundingClientRect()
      ctx.clearRect(0, 0, rect.width, rect.height)

      const dark = isDark()
      const baseColor = getCssColor(dark ? colorDarkVar : colorLightVar)
      const glowColor = getCssColor(dark ? glowColorDarkVar : glowColorLightVar)

      if (backgroundOpacity > 0) {
        ctx.fillStyle = `rgba(0,0,0,${backgroundOpacity})`
        ctx.fillRect(0, 0, rect.width, rect.height)
      }

      const t = now * 0.001 * speedScale

      for (const dot of dots) {
        const pulse = 0.5 + 0.5 * Math.sin(t * dot.speed + dot.phase)
        ctx.globalAlpha = opacity * (0.1 + 0.2 * pulse)
        if (pulse > 0.78) {
          ctx.shadowColor = glowColor
          ctx.shadowBlur = 10 * pulse
          ctx.fillStyle = glowColor
        } else {
          ctx.shadowBlur = 0
          ctx.fillStyle = baseColor
        }
        ctx.beginPath()
        ctx.arc(dot.x, dot.y, radius, 0, Math.PI * 2)
        ctx.fill()
      }
      ctx.globalAlpha = 1
      ctx.shadowBlur = 0

      raf = requestAnimationFrame(draw)
    }

    setup()
    const ro = new ResizeObserver(setup)
    ro.observe(canvas)
    raf = requestAnimationFrame(draw)

    return () => {
      cancelAnimationFrame(raf)
      ro.disconnect()
    }
  }, [
    opacity,
    gap,
    radius,
    colorLightVar,
    glowColorLightVar,
    colorDarkVar,
    glowColorDarkVar,
    backgroundOpacity,
    speedMin,
    speedMax,
    speedScale,
  ])

  return (
    <canvas
      ref={canvasRef}
      className={cn("absolute inset-0 h-full w-full", className)}
      style={style}
    />
  )
}
